import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * The app's own confirmation, in place of window.confirm.
 *
 * Deleting a shoot is the only destructive thing m3cull can do, and it was
 * asking about it through a native browser dialog: a different typeface, a
 * different set of buttons, and on some browsers a checkbox that lets someone
 * switch the question off permanently without m3cull ever knowing. A dialog
 * that can be silenced is not a safeguard.
 *
 * Escape and the cancel button both mean no, and the cancel button holds focus,
 * so hitting Return by reflex never destroys anything.
 */
export default function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancel = useRef<HTMLButtonElement>(null);

  useEffect(() => cancel.current?.focus(), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/80 p-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="w-full max-w-sm rounded-2xl border border-ink-800 bg-ink-900 p-7 shadow-2xl">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-reject-soft text-reject">
          <AlertTriangle size={18} />
        </span>

        <h2 id="confirm-title" className="mt-5 text-head font-semibold text-ink-50">
          {title}
        </h2>

        <div className="mt-3 text-body leading-relaxed text-ink-400">{body}</div>

        <div className="mt-7 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <button
            ref={cancel}
            className="btn-ghost justify-center px-5 py-2.5 text-body"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="btn justify-center border border-reject-line bg-reject-soft px-5 py-2.5 text-body text-reject hover:border-reject active:bg-reject-soft/70"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
